import axios from 'axios';
import { useState } from 'react';
import { useNavigate, Navigate, } from 'react-router-dom';
import {
    TextField,
    IconButton,
    InputAdornment,
    CircularProgress,
    Button
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import Header from './Header';
import '../styles/auth.css';

const API_BASE = process.env.REACT_APP_API_BASE || '';

/**
 * Login renders the authentication form for fleet users.
 * 
 * It validates basic email/password input, calls the login API, and 
 * updates the global auth state before redirecting to the dashboard.
 * 
 * @returns {JSX.Element}
 */
export default function Login() {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [message, setMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const { user, login, loadingUser, isAuthenticated } = useAuth();

    if (loadingUser) {
        return (
            <div className="loading-user">
                <CircularProgress />
            </div>
        );
    }

    if (!loadingUser && isAuthenticated) {
        return <Navigate to="/fleet-dashboard" replace />;
    }

    /**
     * Handles input changes for email and password fields.
     * Updates the "formData" state using the input's "name" as the key.
     * 
     * @param {React.ChangeEvent<HTMLInputElement>} e 
     */
    const onChange = (e) => {
        setFormData((s) => ({ ...s, [e.target.name]: e.target.value }));
    }

    /**
     * Submits the login form, performsbasic validation, and calls the 
     * backend login endpoint.
     * 
     * On success, it stores the access token via "login" and redirects to the
     * fleet dashboard. Otherwise, it sets an error message.
     * 
     * @param {React.FormEvent<HTMLFormElement>} e 
     */
    const onSubmit = async (e) => {
        e.preventDefault();
        const { email, password } = formData;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            setMessage('Please enter a valid email address.');
            return;
        }
        if (!password || !password.trim()) {
            setMessage('Please enter your password.');
            return;
        }

        setLoading(true);
        setMessage('');

        try {
            const { data } = await axios.post(`${API_BASE}/api/auth/login`, 
                { email, password },
                { withCredentials: true }
            );

            const accessToken = data?.accessToken || data?.token;
            if (accessToken) {
                login(accessToken);
                navigate('/fleet-dashboard', { replace: true });
            } else {
                setMessage(data?.message || 'Login failed.')
            }
        } catch (error) {
            setMessage(error?.response?.data?.message || 'Login failed.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <Header />
            <div className="auth-container">
                <div className="auth-box">
                    <h1>Login</h1>
                    <p className="required-fields">* These fields are required.</p>

                    <form onSubmit={onSubmit}>
                        <div className="form-group-auth">
                            <TextField
                                label="Email"
                                name="email"
                                value={formData.email}
                                onChange={onChange}
                                fullWidth
                                margin="normal"
                                required
                                autoComplete="email"
                            />

                            <TextField 
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                name="password"
                                value={formData.password}
                                onChange={onChange}
                                fullWidth
                                margin="normal"
                                required
                                autoComplete="current-password"
                                InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword((s) => !s)}
                                        edge="end"
                                        aria-label="toggle password visibility"
                                    >
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                    </InputAdornment>
                                ),
                                }}
                            />
                            </div>
                            
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                className="login-button"
                                disabled={loading}
                                sx={{ position: 'relative' }}
                            >
                                {loading && (
                                    <CircularProgress
                                        size={20}
                                        thickness={4}
                                        sx={{
                                            position: 'absolute',
                                            left: '50%',
                                            top: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            color: '#fff',
                                        }}
                                    />
                                )}
                                <span style={{ opacity: loading ? 0 : 1 }}>Login</span>
                            </Button>
                    </form>
                    {message && <p className="auth-error">{message}</p>}
                </div>
            </div>
        </div>
    );
}
